import { useEffect, useRef, useState } from 'react';
import * as Logos from '@/assets/stakeholders';

const clamp = (v: number, min = 0, max = 1) => Math.min(max, Math.max(min, v));

// Her logo kendi <a> bağlantısını içeren tek renkli (themeable) bir SVG'dir.
const LOGOS = [
  Logos.SU,
  Logos.NEU,
  Logos.KTUN,
  Logos.KU,
  Logos.KGTU,
  Logos.KTO,
  Logos.Aselsan,
  Logos.Bist,
  Logos.LIMA,
  Logos.Innopark,
  Logos.Atabey,
  Logos.BilimKonya,
  Logos.BilimMerkezi,
  Logos.Divizyon,
  Logos.Kademe,
  Logos.KIEM,
  Logos.MEVKA,
  Logos.SIA,
];

// ── "Küresel amfi" geometrisi ─────────────────────────────────────────────
// Referans görseldeki gibi: yatay yörünge çizgileri merkezde birbirine
// yaklaşır, kenarlara doğru yelpaze gibi açılır (içbükey küre hissi).
// Balonlar da AYNI eğri fonksiyonunun üzerine oturur → çizgi + logo uyumlu.
const arcY = (x: number, off: number) =>
  50 + off * (1 + 1.15 * ((x - 50) / 50) ** 2);

// Çizilecek yörünge bantları (merkezden ofset, %)
const ARC_ROWS = [-26, -16, -6, 4, 14, 24];

// Yerleşim — DÜZENLİ bir ızgara: 3 kavisli sıra × 6 sütun, orta sıra yarım
// adım kaydırılmış. Kavis "dağınık" hissini verir ama diziliş düzenlidir.
const ROW_OFFS = [-15, -1, 13]; // sıra bant ofsetleri (%)
const COLS = 6;
const NODES: { x: number; r: number }[] = ROW_OFFS.flatMap((r, row) =>
  Array.from({ length: COLS }, (_, col) => {
    const step = 84 / (COLS - 1);
    // Orta sıra: yarım adım İÇERİDEN başlar ve İÇERİDEN biter → tuğla
    // kayması korunur ama sıra %50 eksenine göre SİMETRİK kalır (önceden
    // yalnızca sağa kayıyordu, orta sıra merkezden 4-5 puan sağdaydı).
    const x =
      row === 1
        ? 8 + step / 2 + col * ((84 - step) / (COLS - 1))
        : 8 + col * step;
    return { x, r };
  }),
);

// Belirme sırası için sabit karışık dizi (deterministik, ekrana dengeli yayılır).
const ORDER = [0, 9, 14, 5, 7, 16, 3, 12, 2, 11, 6, 15, 4, 13, 8, 17, 1, 10];

// DİKEY (mobil) varyant — aynı geometrinin 90° döndürülmüş hali: yörüngeler
// yukarıdan aşağı akar, merkezde daralıp uçlara doğru yelpaze gibi açılır.
// Dar ekranda taşmaması için kavis daha yumuşak (0.45), bantlar daha geniş.
const arcX = (y: number, off: number) =>
  50 + off * (1 + 0.45 * ((y - 50) / 50) ** 2);

const V_ARC_COLS = [-33, -22, -11, 0, 11, 22, 33]; // çizilecek dikey bantlar
const V_COL_OFFS = [-22, 0, 22]; // 3 sütun bant ofseti (%)
const V_ROWS = 6; // her sütunda 6 logo
const V_NODES: { y: number; r: number }[] = V_COL_OFFS.flatMap((r, col) =>
  Array.from({ length: V_ROWS }, (_, row) => {
    const step = 84 / (V_ROWS - 1);
    // Orta sütun yarım adım kaydırılır → yatay varyanttaki tuğla kayması.
    const y =
      col === 1
        ? 8 + step / 2 + row * ((84 - step) / (V_ROWS - 1))
        : 8 + row * step;
    return { y, r };
  }),
);

// Yörünge çizgileri + yıldız tozları. Eğriler arcY ile birebir aynı
// (quadratic Bézier, y(x) ikinci derece olduğundan tam örtüşür).
const OrbitLines = () => (
  <svg
    aria-hidden
    className="pointer-events-none absolute inset-0 h-full w-full"
    viewBox="0 0 100 100"
    preserveAspectRatio="none"
    fill="none"
  >
    {ARC_ROWS.map((off) => {
      const y0 = arcY(0, off);
      const yMid = arcY(50, off);
      const ctrl = 2 * yMid - y0; // uçlar simetrik → kontrol noktası
      return (
        <path
          key={off}
          d={`M 0 ${y0} Q 50 ${ctrl} 100 ${y0}`}
          stroke="rgba(255,255,255,0.11)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
      );
    })}
    {ORDER.map((n, i) => (
      <circle
        key={i}
        cx={(n * 47 + i * 29) % 100}
        cy={(n * 31 + i * 13) % 100}
        r="0.28"
        fill="rgba(255,255,255,0.3)"
      />
    ))}
  </svg>
);

// Dikey yörünge çizgileri — yatay olanın 90° döndürülmüş eşleniği.
const OrbitLinesV = () => (
  <svg
    aria-hidden
    className="pointer-events-none absolute inset-0 h-full w-full"
    viewBox="0 0 100 100"
    preserveAspectRatio="none"
    fill="none"
  >
    {V_ARC_COLS.map((off) => {
      const x0 = arcX(0, off);
      const xMid = arcX(50, off);
      const ctrl = 2 * xMid - x0;
      return (
        <path
          key={off}
          d={`M ${x0} 0 Q ${ctrl} 50 ${x0} 100`}
          stroke="rgba(255,255,255,0.11)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
      );
    })}
    {ORDER.map((n, i) => (
      <circle
        key={i}
        cx={(n * 31 + i * 13) % 100}
        cy={(n * 47 + i * 29) % 100}
        r="0.28"
        fill="rgba(255,255,255,0.3)"
      />
    ))}
  </svg>
);

const Stakeholders = ({ progress }: { progress?: number }) => {
  const controlled = progress != null; // dış ilerleme (ör. pinli sahne)
  const wrapRef = useRef<HTMLDivElement>(null);
  const [prog, setProg] = useState(0); // scroll ilerlemesi (0→1)

  // Bölüm görünür alana girdikçe ilerleme → balonlar kademeli belirir.
  useEffect(() => {
    if (controlled) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = wrapRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        setProg(clamp((vh - r.top) / (vh * 0.85)));
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [controlled]);

  const effProg = controlled ? clamp(progress ?? 0) : prog;

  return (
    <section
      ref={wrapRef}
      className="relative flex min-h-[70lvh] w-full items-center justify-center overflow-hidden py-10 sm:min-h-[100lvh] sm:py-12"
    >
      {/* Merkezde çok hafif aydınlanan küresel zemin — siyahın içinde derinlik */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 45% at 50% 50%, rgba(255,255,255,0.045), transparent 70%)',
        }}
      />
      {/* Mobil/tablet — masaüstündeki amfi görünümünün 90° döndürülmüş hali:
          yörüngeler dikey akar, logolar 3 sütun × 6 satır olarak yayın üzerine
          oturur. Aynı beyaz logolar, aynı kademeli belirme. */}
      {/* pt: bölüm başlığı ("Paydaşlarımız") sahnenin üstünde duruyor; yay
          onun altından başlasın, logolar başlığın içine girmesin. */}
      <div className="relative mx-auto mt-[16vh] h-[68dvh] min-h-[440px] w-full max-w-md px-2 md:hidden">
        <OrbitLinesV />
        {LOGOS.map((Logo, i) => {
          const node = V_NODES[i];
          const x = arcX(node.y, node.r);
          const rank = ORDER.indexOf(i) / ORDER.length;
          const reveal = clamp((effProg * 1.5 - rank) / 0.45);
          return (
            <div
              key={i}
              className="absolute"
              style={{
                left: `${x}%`,
                top: `${node.y}%`,
                opacity: reveal,
                transform: `translate(-50%, -50%) translateX(${(1 - reveal) * 18}px)`,
                transition: 'opacity 0.8s ease, transform 0.8s ease',
              }}
            >
              <div className="flex h-[44px] w-[96px] items-center justify-center [&_a]:flex [&_a]:h-full [&_a]:w-full [&_a]:items-center [&_a]:justify-center [&_svg]:h-full [&_svg]:w-full [&_svg]:fill-white [&_svg]:opacity-85">
                <Logo />
              </div>
            </div>
          );
        })}
      </div>

      <div className="relative mx-auto hidden h-[72vh] min-h-[480px] w-full max-w-7xl md:block">
        <OrbitLines />
        {LOGOS.map((Logo, i) => {
          const node = NODES[i];
          const left = node.x;
          const top = arcY(node.x, node.r * 1.3);
          // Kademeli belirme: ORDER sırasına göre gecikmeli, yumuşak.
          const rank = ORDER.indexOf(i) / ORDER.length;
          const reveal = clamp((effProg * 1.5 - rank) / 0.45);
          return (
            <div
              key={i}
              className="absolute"
              style={{
                left: `${left}%`,
                top: `${top}%`,
                opacity: reveal,
                transform: `translate(-50%, -50%) translateY(${(1 - reveal) * 26}px)`,
                transition: 'opacity 0.8s ease, transform 0.8s ease',
              }}
            >
              {/* Yalın beyaz logo — daire yok, hepsi aynı boyutta */}
              <div className="flex h-[64px] w-[120px] items-center justify-center transition-opacity duration-300 hover:opacity-100 sm:h-[72px] sm:w-[150px] [&_a]:flex [&_a]:h-full [&_a]:w-full [&_a]:items-center [&_a]:justify-center [&_svg]:h-full [&_svg]:w-full [&_svg]:fill-white [&_svg]:opacity-85 [&_svg]:transition-opacity [&_svg]:duration-300 hover:[&_svg]:opacity-100">
                <Logo />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Stakeholders;
